/**
 * Hierarchy Types
 *
 * Purpose: Type definitions for hierarchical field rendering and tree navigation
 * Architecture: Shared types used across hierarchy components and utilities
 * Author: Development Team
 * Created: 2025-01-21
 */

import type { MasterDataValue } from './mdm';
import type { FieldDefinition } from '../services/metadataService';

/**
 * Tree node data structure
 *
 * Represents a single node in the hierarchy tree with its metadata,
 * children, and loading state.
 */
export interface TreeNode {
  /**
   * Unique identifier for the node (master data value ID)
   */
  id: string;

  /**
   * The underlying master data value
   */
  value: MasterDataValue;

  /**
   * Child nodes in the hierarchy
   */
  children: TreeNode[];

  /**
   * Field definition ID this node belongs to
   */
  fielddef_id: string;

  /**
   * Hierarchy level (1 = root, 2 = first child level, etc.)
   */
  level: number;

  /**
   * Whether children have been loaded for this node
   */
  loaded: boolean;

  /**
   * Whether children are currently being loaded
   */
  loading: boolean;
}

/**
 * RichTreeView item structure (nested children)
 *
 * Used by Material-UI RichTreeView component for rendering
 */
export type RichItem = {
  /**
   * Unique identifier for the tree item
   */
  id: string;

  /**
   * Display label for the tree item
   */
  label: string;

  /**
   * Optional nested children
   */
  children?: RichItem[];
};

/**
 * Constants for timing and configuration
 */
export const HIERARCHY_CONFIG = {
  /**
   * Debug mode flag (disabled in production)
   */
  DEBUG: false,

  /**
   * Debounce delay for search input (milliseconds)
   */
  DEBOUNCE_DELAY_MS: 300,

  /**
   * Delay before loading tree nodes (milliseconds)
   */
  TREE_LOAD_DELAY_MS: 100,

  /**
   * Delay before scrolling to node (milliseconds)
   */
  SCROLL_DELAY_MS: 500,

  /**
   * Duration to highlight search matches (milliseconds)
   */
  HIGHLIGHT_DURATION_MS: 2000,
} as const;

/**
 * Hierarchy chain result
 *
 * Represents the chain of field definitions from root to the target level
 */
export interface HierarchyChain {
  /**
   * Ordered array of field definitions from root (level 1) to target level
   */
  fieldDefinitions: FieldDefinition[];

  /**
   * Target field definition (the one being edited)
   */
  targetField: FieldDefinition;

  /**
   * Maximum hierarchy level in the chain
   */
  maxLevel: number;
}

/**
 * Node cache entry
 *
 * Stores cached tree nodes for efficient retrieval
 */
export interface NodeCacheEntry {
  /**
   * The cached tree node
   */
  node: TreeNode;

  /**
   * Timestamp when the node was cached
   */
  cachedAt: number;
}

/**
 * Search result
 *
 * Represents a node that matches a search query
 */
export interface SearchResult {
  /**
   * Node ID that matched
   */
  nodeId: string;

  /**
   * The matched node
   */
  node: TreeNode;

  /**
   * Path from root to this node (array of node IDs)
   */
  path: string[];

  /**
   * Match score (higher is better match)
   */
  score: number;
}
